/**
 * Утилиты для оптимизации памяти при работе с большими JSON объектами в Pinia
 */

/**
 * Создает WeakMap для кэширования замороженных объектов
 */
const frozenCache = new WeakMap()

/**
 * Консервативная глубокая заморозка с ограничением глубины
 * @param {any} obj - объект для заморозки
 * @param {number} maxDepth - максимальная глубина заморозки
 * @returns {any} замороженный объект
 */
export function conservativeDeepFreeze(obj, maxDepth = 3) {
  if (obj === null || typeof obj !== 'object' || maxDepth <= 0) return obj

  // Проверяем кэш
  if (frozenCache.has(obj)) {
    return frozenCache.get(obj)
  }

  // Замораживаем объект
  Object.freeze(obj)
  frozenCache.set(obj, obj)

  // Рекурсивно замораживаем свойства только до определенной глубины
  if (maxDepth > 1) {
    Object.values(obj).forEach(value => {
      if (value !== null && typeof value === 'object') {
        conservativeDeepFreeze(value, maxDepth - 1)
      }
    })
  }

  return obj
}

/**
 * Оптимизированная глубокая заморозка с кэшированием
 * @param {any} obj - объект для заморозки
 * @returns {any} замороженный объект
 */
export function optimizedDeepFreeze(obj) {
  // Используем консервативную заморозку для больших объектов
  return conservativeDeepFreeze(obj, 2)
}

/**
 * Безопасная заморозка для больших массивов
 * @param {Array} arr - массив для заморозки
 * @returns {Array} безопасно замороженный массив
 */
export function safeFreeze(arr) {
  if (!Array.isArray(arr)) return Object.freeze(arr)

  // Создаем копию массива и замораживаем только верхний уровень объектов
  const result = arr.map(item => {
    if (typeof item === 'object' && item !== null) {
      // Создаем поверхностную копию объекта перед заморозкой
      const itemCopy = { ...item }
      return Object.freeze(itemCopy)
    }
    return item
  })

  return Object.freeze(result)
}

/**
 * Ленивая заморозка для очень больших массивов (устаревшая - использовать safeFreeze)
 * @param {Array} arr - массив для заморозки
 * @returns {Array} оптимизированный массив
 * @deprecated Используйте safeFreeze вместо этого метода
 */
export function lazyFreezeArray(arr) {
  // Используем безопасную заморозку вместо ленивой
  return safeFreeze(arr)
}

/**
 * Дедупликация строк для экономии памяти
 */
const stringCache = new Map()

export function deduplicateStrings(obj) {
  if (typeof obj === 'string') {
    if (!stringCache.has(obj)) {
      stringCache.set(obj, obj)
    }
    return stringCache.get(obj)
  }

  if (Array.isArray(obj)) {
    return obj.map(deduplicateStrings)
  }

  if (obj !== null && typeof obj === 'object') {
    const result = {}
    for (const [key, value] of Object.entries(obj)) {
      const dedupKey = stringCache.has(key)
        ? stringCache.get(key)
        : (() => {
            stringCache.set(key, key)
            return key
          })()
      result[dedupKey] = deduplicateStrings(value)
    }
    return result
  }

  return obj
}

/**
 * Создает read-only view для больших объектов
 * @param {any} data - исходные данные
 * @returns {Proxy} read-only прокси
 */
export function createReadOnlyView(data) {
  return new Proxy(data, {
    get(target, prop) {
      const value = target[prop]
      if (typeof value === 'object' && value !== null) {
        return createReadOnlyView(value)
      }
      return value
    },
    set() {
      throw new Error('Attempt to modify read-only data')
    },
    deleteProperty() {
      throw new Error('Attempt to delete property from read-only data')
    },
  })
}

/**
 * Сжатие объектов путем удаления undefined и null значений
 * @param {any} obj - объект для сжатия
 * @returns {any} сжатый объект
 */
export function compressObject(obj) {
  if (Array.isArray(obj)) {
    return obj.map(compressObject).filter(item => item !== undefined && item !== null)
  }

  if (obj !== null && typeof obj === 'object') {
    const compressed = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined && value !== null) {
        const compressedValue = compressObject(value)
        if (compressedValue !== undefined && compressedValue !== null) {
          compressed[key] = compressedValue
        }
      }
    }
    return compressed
  }

  return obj
}

/**
 * Статистика использования памяти
 */
export const memoryStats = {
  frozenObjectsCount: () => frozenCache.size || 0,
  stringCacheSize: () => stringCache.size || 0,
  clearCaches() {
    stringCache.clear()

    // frozenCache нельзя очистить, так как это WeakMap
  },
}
